import { browserErrorDetails, diagnosticBuild, scriptLocation } from "./diagnostics.js";

const USER_STORAGE_KEY = "glc.analytics.user";
const SESSION_STORAGE_KEY = "glc.analytics.session";
const SEARCH_STORAGE_KEY = "glc.analytics.search";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const MAX_SEARCH_LENGTH = 80;
const SEARCH_INPUT_DEBOUNCE_MS = 350;

function randomId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID().replaceAll("-", "");
  const values = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(values);
  if (values.some(Boolean)) return Array.from(values, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.padEnd(16, "0").slice(0, 32);
}

function storedUserId() {
  try {
    const existing = localStorage.getItem(USER_STORAGE_KEY);
    if (existing) return existing;
    const created = randomId();
    localStorage.setItem(USER_STORAGE_KEY, created);
    return created;
  } catch {
    return randomId();
  }
}

function activeSession(now) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) || "null");
    const recent = saved
      && typeof saved.id === "string"
      && Number.isFinite(saved.lastActivityAt)
      && now >= saved.lastActivityAt
      && now - saved.lastActivityAt < SESSION_IDLE_MS;
    const session = recent ? saved : { id: randomId(), lastActivityAt: now };
    session.lastActivityAt = now;
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    return session.id;
  } catch {
    return randomId();
  }
}

function touchSession(id, now) {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id, lastActivityAt: now }));
  } catch { /* Analytics still works when storage is unavailable. */ }
}

function browserFamily() {
  const agent = navigator.userAgent || "";
  if (/Edg\//.test(agent)) return "Edge";
  if (/Firefox\//.test(agent)) return "Firefox";
  if (/Chrome\//.test(agent)) return "Chrome";
  if (/Safari\//.test(agent)) return "Safari";
  return "Other";
}

function deviceCategory() {
  if (navigator.userAgentData?.mobile || matchMedia("(max-width: 699px)").matches) return "mobile";
  if (matchMedia("(max-width: 1024px)").matches) return "tablet";
  return "desktop";
}

function viewportBucket() {
  const width = window.innerWidth;
  if (width < 480) return "under-480";
  if (width < 700) return "480-699";
  if (width < 1024) return "700-1023";
  if (width < 1440) return "1024-1439";
  return "1440-plus";
}

function referrerHost() {
  if (!document.referrer) return "Direct / unavailable";
  try { return new URL(document.referrer).hostname.toLowerCase(); } catch { return "Direct / unavailable"; }
}

function pageContext() {
  return {
    device: deviceCategory(),
    browser: browserFamily(),
    viewport: viewportBucket(),
    connection: navigator.connection?.effectiveType || "",
  };
}

function cleanSearch(value) {
  return String(value || "").trim().replace(/\s+/g, " ").slice(0, MAX_SEARCH_LENGTH);
}

function defaultTransport(payload) {
  const body = JSON.stringify(payload);
  if (navigator.sendBeacon) {
    const queued = navigator.sendBeacon(
      "/api/v1/analytics/event",
      new Blob([body], { type: "application/json" }),
    );
    if (queued) return;
  }
  fetch("/api/v1/analytics/event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    credentials: "same-origin",
  }).catch(() => {});
}

export class AnonymousAnalytics {
  constructor({ now = () => Date.now(), transport = defaultTransport } = {}) {
    this.now = now;
    this.transport = transport;
    this.userId = storedUserId();
    this.sessionId = activeSession(this.now());
    this.visible = document.visibilityState !== "hidden";
    this.pageStartedAt = this.visible ? this.now() : null;
    this.layer = null;
    this.layerStartedAt = null;
    this.waterColumn = null;
    this.waterColumnStartedAt = null;
    this.startedAt = this.now();
    this.lastSearchAt = null;
    this.lastSearchHadQuery = false;
    this.lastSearchQuery = "";
    this.searchInputTimer = null;
    this.pendingSearchForm = null;
    this.waterInteractions = new Set();
    this.mapReadySent = false;
  }

  send(eventType, detail = {}) {
    if (location.pathname.startsWith("/admin")) return;
    const preciseLocation = eventType === "location_found";
    this.transport({
      ...((eventType === "client_error" || (eventType === "api_request" && detail.success === false)) ? { build: diagnosticBuild() } : {}),
      eventType,
      path: location.pathname,
      ...(preciseLocation ? {} : { sessionId: this.sessionId, anonymousUserId: this.userId }),
      ...detail,
    });
    if (!preciseLocation) touchSession(this.sessionId, this.now());
  }

  flushDuration(eventType, startedAt, detail = {}) {
    if (startedAt === null) return;
    const durationMs = Math.max(0, Math.round(this.now() - startedAt));
    if (durationMs > 0) this.send(eventType, { ...detail, durationMs });
  }

  flushVisibleActivity() {
    this.flushDuration("page_engagement", this.pageStartedAt);
    this.pageStartedAt = null;
    if (this.layer) {
      this.flushDuration("layer_view", this.layerStartedAt, { layer: this.layer });
      this.layerStartedAt = null;
    }
    if (this.waterColumn) {
      this.flushDuration("water_column_engagement", this.waterColumnStartedAt, this.waterColumn);
      this.waterColumnStartedAt = null;
    }
  }

  resumeVisibleActivity() {
    if (this.visible) return;
    this.visible = true;
    const now = this.now();
    const resumedSession = activeSession(now);
    if (resumedSession !== this.sessionId) {
      this.sessionId = resumedSession;
      this.send("page_view", { ...pageContext(), referrerHost: "Direct / unavailable" });
    }
    this.pageStartedAt = now;
    if (this.layer) this.layerStartedAt = now;
    if (this.waterColumn) this.waterColumnStartedAt = now;
  }

  changeLayer(detail) {
    const layer = detail?.layer;
    if (typeof layer !== "string") return;
    if (this.layer) this.flushDuration("layer_view", this.layerStartedAt, { layer: this.layer });
    this.layer = layer || null;
    this.layerStartedAt = this.visible && this.layer ? this.now() : null;
    if (this.layer) {
      const overlays = [
        document.querySelector("[data-bathymetry-contours]")?.checked ? "contours" : "",
        document.querySelector("[data-bathymetry-raster]")?.checked ? "depth_shading" : "",
        document.querySelector("[data-bathymetry-raster-v2]")?.checked ? "depth_shading_v2" : "",
      ].filter(Boolean).join("+") || "none";
      this.send("layer_change", { layer: this.layer, overlays });
    }
  }

  openWaterColumn(detail) {
    if (this.waterColumn) {
      this.flushDuration("water_column_engagement", this.waterColumnStartedAt, this.waterColumn);
    }
    this.waterColumn = {
      lake: typeof detail?.lake === "string" ? detail.lake : "",
      model: typeof detail?.model === "string" ? detail.model : "",
    };
    this.waterInteractions.clear();
    this.waterColumnStartedAt = this.visible ? this.now() : null;
    this.send("water_column_view", this.waterColumn);
  }

  closeWaterColumn() {
    if (!this.waterColumn) return;
    this.flushDuration("water_column_engagement", this.waterColumnStartedAt, this.waterColumn);
    this.waterColumn = null;
    this.waterColumnStartedAt = null;
  }

  clearSearchState() {
    this.lastSearchAt = null;
    this.lastSearchHadQuery = false;
    this.lastSearchQuery = "";
    try {
      sessionStorage.removeItem(SEARCH_STORAGE_KEY);
    } catch { /* Search analytics still works when storage is unavailable. */ }
  }

  restoreSearchState(form) {
    const input = form.querySelector("input[type='search']");
    const query = cleanSearch(input?.value);
    if (!query) {
      this.clearSearchState();
      return;
    }
    try {
      const saved = JSON.parse(sessionStorage.getItem(SEARCH_STORAGE_KEY) || "null");
      const now = this.now();
      if (saved?.query !== query || !Number.isFinite(saved.startedAt)
        || now < saved.startedAt || now - saved.startedAt >= SESSION_IDLE_MS) {
        this.clearSearchState();
        return;
      }
      this.lastSearchAt = saved.startedAt;
      this.lastSearchHadQuery = true;
      this.lastSearchQuery = query;
    } catch {
      this.clearSearchState();
    }
  }

  flushPendingSearch() {
    if (!this.pendingSearchForm) return;
    window.clearTimeout(this.searchInputTimer);
    const form = this.pendingSearchForm;
    this.searchInputTimer = null;
    this.pendingSearchForm = null;
    this.recordSearch(form, { live: true });
  }

  recordSearch(form, { live = false } = {}) {
    const input = form.querySelector("input[type='search']");
    if (!input) return;
    const query = cleanSearch(input.value);
    const resultCount = document.querySelectorAll("[data-port-search]:not([hidden])").length;
    if (!query && live) {
      this.clearSearchState();
      return;
    }
    if (query && query === this.lastSearchQuery && this.lastSearchAt !== null) return;

    this.lastSearchAt = query ? this.now() : null;
    this.lastSearchHadQuery = Boolean(query);
    this.lastSearchQuery = query;
    if (query) {
      try {
        sessionStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify({ query, startedAt: this.lastSearchAt }));
      } catch { /* Search analytics still works when storage is unavailable. */ }
    } else {
      this.clearSearchState();
    }
    this.send("search_performed", { query, resultCount });
    if (query && resultCount === 0) this.send("search_no_results", { query });
  }

  recordPageTiming() {
    const navigation = performance.getEntriesByType?.("navigation")?.[0];
    const durationMs = Number(navigation?.loadEventEnd || navigation?.duration);
    if (Number.isFinite(durationMs) && durationMs > 0) {
      this.send("page_load_timing", { metric: "page_load", durationMs });
    }
  }

  recordWaterInteraction(detail) {
    if (!this.waterColumn || typeof detail?.action !== "string") return;
    const key = `${detail.action}:${detail.value ?? ""}`;
    if (detail.action === "chart_inspected" && this.waterInteractions.has(key)) return;
    this.waterInteractions.add(key);
    this.send("water_column_interaction", {
      ...this.waterColumn,
      action: detail.action,
      value: String(detail.value ?? ""),
    });
  }

  start() {
    if (location.pathname.startsWith("/admin")) return this;
    const portSearchForm = document.querySelector("#port-search")?.closest("form[role='search']");
    if (portSearchForm) this.restoreSearchState(portSearchForm);
    this.send("page_view", { ...pageContext(), referrerHost: referrerHost() });
    const linkedView = new URLSearchParams(location.hash.replace(/^#/, ""));
    if (location.pathname === "/map" && linkedView.get("shared") === "1") {
      const layer = linkedView.get("layer") || "";
      this.send("share_link_open", { layer, source: "share_button" });
    }
    document.addEventListener("great-lakes-layer-change", (event) => this.changeLayer(event.detail));
    document.addEventListener("great-lakes-map-click", (event) => {
      if (event.detail?.lake) {
        this.send("lake_click", {
          lake: event.detail.lake,
          source: "map",
          zoom: Number(event.detail.zoom) || 0,
        });
      }
    });
    document.addEventListener("great-lakes-map-interaction", (event) => {
      this.send("map_interaction", event.detail || {});
    });
    document.addEventListener("great-lakes-geolocation-found", (event) => {
      this.send("location_found", event.detail || {});
    });
    document.addEventListener("great-lakes-measure", (event) => {
      this.send("measure_tool", event.detail || {});
    });
    document.addEventListener("great-lakes-map-ready", () => {
      if (this.mapReadySent) return;
      this.mapReadySent = true;
      this.send("map_ready", { durationMs: this.now() - this.startedAt });
    });
    document.addEventListener("great-lakes-three-d-ready", (event) => {
      this.send("three_d_ready", event.detail || {});
    });
    document.addEventListener("great-lakes-three-d-interaction", (event) => {
      this.send("three_d_interaction", event.detail || {});
    });
    document.addEventListener("great-lakes-three-d-issue", (event) => {
      this.send("three_d_issue", event.detail || {});
    });
    document.addEventListener("great-lakes-forecast-change", (event) => {
      this.send("forecast_changed", event.detail || {});
    });
    document.addEventListener("great-lakes-depth-change", (event) => {
      this.send("depth_changed", event.detail || {});
    });
    document.addEventListener("great-lakes-water-column-open", (event) => this.openWaterColumn(event.detail));
    document.addEventListener("great-lakes-water-column-close", () => this.closeWaterColumn());
    document.addEventListener("great-lakes-water-column-interaction", (event) => this.recordWaterInteraction(event.detail));
    document.addEventListener("great-lakes-api-request", (event) => {
      const detail = event.detail || {};
      this.send("api_request", { ...(detail.success === false ? pageContext() : {}), ...detail });
    });
    document.addEventListener("great-lakes-share-copy", (event) => {
      const detail = event.detail || {};
      this.send("share_link_copy", {
        lake: typeof detail.lake === "string" ? detail.lake : "",
        layer: typeof detail.layer === "string" ? detail.layer : "",
      });
    });
    document.addEventListener("great-lakes-share-result", (event) => {
      this.send("share_link_result", event.detail || {});
    });
    document.addEventListener("great-lakes-client-error", (event) => {
      this.send("client_error", { ...pageContext(), ...event.detail });
    });
    document.addEventListener("submit", (event) => {
      if (event.target.matches(".mailing-list-form")) {
        // Only attach attribution when analytics is enabled. Never send the email
        // to the analytics endpoint; the server records a confirmed signup.
        for (const [name, value] of Object.entries({
          analytics_user: this.userId,
          analytics_session: this.sessionId,
          analytics_path: location.pathname,
        })) {
          let field = event.target.querySelector(`input[name="${name}"]`);
          if (!field) {
            field = document.createElement("input");
            field.type = "hidden";
            field.name = name;
            event.target.append(field);
          }
          field.value = value;
        }
      }
      if (event.target.matches("form[role='search']")) {
        this.flushPendingSearch();
        this.recordSearch(event.target);
      }
    });
    document.addEventListener("input", (event) => {
      if (event.target.id !== "port-search") return;
      const form = event.target.closest("form[role='search']");
      if (!form) return;
      window.clearTimeout(this.searchInputTimer);
      this.pendingSearchForm = form;
      this.searchInputTimer = window.setTimeout(() => this.flushPendingSearch(), SEARCH_INPUT_DEBOUNCE_MS);
    });
    document.addEventListener("change", (event) => {
      // The 3D viewer reuses the units control but reports its own controls.
      if (location.pathname === "/3d") return;
      const control = event.target.closest("[data-basemap], [data-units], [data-bathymetry-contours], [data-bathymetry-raster], [data-bathymetry-raster-v2], [data-gl-target-selection], [data-gl-target-temperature]");
      if (!control) return;
      const names = [
        ["data-basemap", "basemap"],
        ["data-units", "units"],
        ["data-bathymetry-contours", "depth_contours"],
        ["data-bathymetry-raster", "depth_shading"],
        ["data-bathymetry-raster-v2", "depth_shading_v2"],
        ["data-gl-target-selection", "target_selection"],
        ["data-gl-target-temperature", "target_temperature"],
      ];
      const name = names.find(([attribute]) => control.hasAttribute(attribute))?.[1];
      if (!name) return;
      this.send("control_changed", {
        control: name,
        value: control.type === "checkbox" ? String(control.checked) : control.value,
      });
    });
    document.addEventListener("click", (event) => {
      const result = event.target.closest("[data-port-search] a");
      if (result) {
        this.flushPendingSearch();
        this.send("lake_selected", {
          lake: result.textContent.trim(),
          source: this.lastSearchHadQuery || cleanSearch(document.querySelector("input[type='search']")?.value) ? "search_results" : "directory",
          durationMs: this.lastSearchAt === null ? 0 : this.now() - this.lastSearchAt,
        });
      }
      const navigation = event.target.closest("a[href]");
      if (navigation) {
        const destination = new URL(navigation.href, location.origin);
        if (destination.origin === location.origin && !destination.pathname.startsWith("/admin")
          && destination.pathname !== location.pathname) {
          this.send("navigation_click", { destination: destination.pathname });
        }
      }
      const mapLink = event.target.closest("a[href^='/map#']");
      if (mapLink && !result) {
        this.send("lake_selected", {
          lake: mapLink.textContent.trim(),
          source: "content_link",
          durationMs: 0,
        });
      }
      const download = event.target.closest("a[download], [data-analytics-export]");
      if (download) {
        const resource = download.dataset.analyticsExport || download.getAttribute("href")?.split("/").at(-1) || "download";
        const format = resource.includes(".") ? resource.split(".").at(-1) : "";
        this.send("data_export", { resource, format });
      }
      const feedback = event.target.closest("[data-analytics-feedback]");
      if (feedback) {
        this.send("feature_feedback", {
          feature: feedback.dataset.analyticsFeature || "site",
          rating: feedback.dataset.analyticsFeedback,
        });
        feedback.closest("[data-analytics-feedback-group]")?.setAttribute("data-submitted", "true");
      }
    });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.visible = false;
        this.flushVisibleActivity();
      } else {
        this.resumeVisibleActivity();
      }
    });
    // The map and the 3D viewer are full-screen scenes with nothing to scroll.
    if (location.pathname !== "/map" && location.pathname !== "/3d") {
      const scrollMilestones = new Set();
      window.addEventListener("scroll", () => {
        if (document.visibilityState === "hidden") return;
        const height = document.documentElement.scrollHeight - window.innerHeight;
        if (height <= 0) return;
        const percent = Math.min(100, Math.round(window.scrollY * 100 / height));
        for (const milestone of [25, 50, 75, 90]) {
          if (percent >= milestone && !scrollMilestones.has(milestone)) {
            scrollMilestones.add(milestone);
            this.send("content_scroll", { percent: milestone });
          }
        }
      }, { passive: true });
    }
    window.addEventListener("pagehide", () => {
      this.visible = false;
      this.flushVisibleActivity();
    });
    window.addEventListener("pageshow", () => this.resumeVisibleActivity());
    window.addEventListener("error", (event) => {
      this.send("client_error", {
        ...pageContext(),
        ...browserErrorDetails(event.error, event.message),
        source: scriptLocation(event.filename) || "window",
        line: event.lineno || 0,
        column: event.colno || 0,
        online: navigator.onLine !== false,
      });
    });
    window.addEventListener("unhandledrejection", (event) => {
      if (event.reason?.name === "AbortError") return;
      this.send("client_error", {
        ...pageContext(),
        ...browserErrorDetails(event.reason),
        source: "promise",
        online: navigator.onLine !== false,
      });
    });
    if (document.readyState === "complete") this.recordPageTiming();
    else window.addEventListener("load", () => this.recordPageTiming(), { once: true });
    return this;
  }
}

let analytics;

export function setupAnalytics() {
  if (analytics || location.pathname.startsWith("/admin")) return analytics;
  if (navigator.doNotTrack === "1" || navigator.globalPrivacyControl === true) return null;
  analytics = new AnonymousAnalytics().start();
  return analytics;
}
